import { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

interface ImageLightboxProps {
  src: string;
  onClose: () => void;
}

export function ImageLightbox({ src, onClose }: ImageLightboxProps) {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isRightDragging, setIsRightDragging] = useState(false);

  // Refs for drag (avoid stale closures in global event listeners)
  const dragStart = useRef({ x: 0, y: 0 });
  const dragOrigin = useRef({ x: 0, y: 0 });
  const hasDragged = useRef(false);
  const scaleRef = useRef(1);
  const offsetRef = useRef({ x: 0, y: 0 });
  const imgWrapperRef = useRef<HTMLDivElement>(null);

  // Keep refs in sync
  useEffect(() => { scaleRef.current = scale; }, [scale]);
  useEffect(() => { offsetRef.current = offset; }, [offset]);

  // Prevent body scroll while lightbox is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ── Desktop: global mouse events during left-drag ───────────────────────
  useEffect(() => {
    if (!isRightDragging) return;

    const onMove = (e: MouseEvent) => {
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) hasDragged.current = true;
      if (hasDragged.current) {
        setOffset({ x: dragOrigin.current.x + dx, y: dragOrigin.current.y + dy });
      }
    };

    const onUp = (e: MouseEvent) => {
      if (e.button !== 0) return;
      if (!hasDragged.current) {
        // Left-click without drag → zoom in
        setScale(s => Math.min(s + 0.8, 6));
      }
      hasDragged.current = false;
      setIsRightDragging(false);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isRightDragging]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    dragStart.current = { x: e.clientX, y: e.clientY };
    dragOrigin.current = { ...offsetRef.current };
    hasDragged.current = false;
    setIsRightDragging(true);
  };

  // Right-click on image → reset scale/offset
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // no-op here; zoom handled in mouseup
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setScale(1);
    setOffset({ x: 0, y: 0 });
  };

  // ── Mobile: touch events ────────────────────────────────────────────────
  const lastTouchDistRef = useRef<number | null>(null);
  const lastTouchPosRef = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.stopPropagation();
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastTouchDistRef.current = Math.sqrt(dx * dx + dy * dy);
      lastTouchPosRef.current = null;
    } else if (e.touches.length === 1) {
      lastTouchPosRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      lastTouchDistRef.current = null;
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    lastTouchDistRef.current = null;
    lastTouchPosRef.current = null;
  }, []);

  // Non-passive touchmove for pinch/pan
  useEffect(() => {
    const el = imgWrapperRef.current;
    if (!el) return;

    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      if (e.touches.length === 2 && lastTouchDistRef.current !== null) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const ratio = dist / lastTouchDistRef.current;
        lastTouchDistRef.current = dist;
        setScale(s => {
          const next = Math.min(Math.max(s * ratio, 0.5), 6);
          scaleRef.current = next;
          return next;
        });
      } else if (e.touches.length === 1 && lastTouchPosRef.current && scaleRef.current > 1.05) {
        const dx = e.touches[0].clientX - lastTouchPosRef.current.x;
        const dy = e.touches[0].clientY - lastTouchPosRef.current.y;
        lastTouchPosRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        setOffset(o => ({ x: o.x + dx, y: o.y + dy }));
      }
    };

    el.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => el.removeEventListener('touchmove', onTouchMove);
  }, []);

  return (
    <div
      className="fixed inset-0 z-[300] bg-black/85 flex items-center justify-center overflow-hidden touch-none select-none"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        className="fixed top-5 right-5 z-[310] p-3 bg-white/15 hover:bg-white/30 text-white rounded-full backdrop-blur-md transition-all shadow-xl active:scale-90"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
      >
        <X size={22} />
      </button>

      {/* Desktop hint */}
      <p className="fixed bottom-5 left-1/2 -translate-x-1/2 text-white/40 text-[11px] font-medium pointer-events-none whitespace-nowrap hidden md:block">
        左鍵放大 · 左鍵拖曳移動 · 右鍵重置
      </p>
      {/* Mobile hint */}
      <p className="fixed bottom-5 left-1/2 -translate-x-1/2 text-white/40 text-[11px] font-medium pointer-events-none whitespace-nowrap md:hidden">
        雙指縮放 · 單指拖曳
      </p>

      {/* Image wrapper */}
      <div
        ref={imgWrapperRef}
        style={{
          transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
          transition: isRightDragging ? 'none' : 'transform 0.15s ease',
          cursor: isRightDragging ? 'grabbing' : scale > 1 ? 'grab' : 'default',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onContextMenu={handleContextMenu}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <img
          src={src}
          className="max-w-[90vw] max-h-[85vh] rounded-xl object-contain shadow-2xl block"
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
        />
      </div>
    </div>
  );
}
